"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { RotationGateModal } from "@/components/rotation-gate/RotationGateModal";
import { setRotationGateHandler } from "@/lib/rotation-gate";

interface RotationGateContextType {
  isOpen: boolean;
  /** `{ dismissible: true }` pour une rotation volontaire (ex: Paramètres > Sécurité). */
  open: (options?: { dismissible?: boolean }) => void;
  close: () => void;
}

const RotationGateContext = createContext<RotationGateContextType | undefined>(
  undefined,
);

export function RotationGateProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissible, setDismissible] = useState(false);

  const open = useCallback((options?: { dismissible?: boolean }) => {
    setDismissible(!!options?.dismissible);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // Enregistre le handler auprès du bus d'événements. Le handler est appelé
  // par l'intercepteur Axios quand un 403 bootstrap-pending est capturé —
  // ce cas est toujours forcé (non-dismissible).
  useEffect(() => {
    setRotationGateHandler(() => {
      setDismissible(false);
      setIsOpen(true);
    });
    return () => setRotationGateHandler(null);
  }, []);

  return (
    <RotationGateContext.Provider value={{ isOpen, open, close }}>
      {children}
      <RotationGateModal isOpen={isOpen} onClose={close} dismissible={dismissible} />
    </RotationGateContext.Provider>
  );
}

export function useRotationGate() {
  const context = useContext(RotationGateContext);
  if (!context) {
    throw new Error(
      "useRotationGate must be used within a RotationGateProvider",
    );
  }
  return context;
}